import React from 'react';
import { motion } from 'framer-motion';

/**
 * Premium Card Component
 * A luxury card design for the habitué network
 * No type labels (gift/loyalty/payment) - it's ONE card for everything
 */
const PremiumCard = ({ 
  balance = null, 
  tier = 'Member',
  cardNumber = '•••• 4821',
  showBalance = true,
  size = 'md',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'max-w-xs',
    md: 'max-w-md',
    lg: 'max-w-lg'
  };

  const tierColors = {
    'Member': '#8BA89A',
    'Reserve': '#C0C0C0', 
    'Founding': '#C3A365'
  };

  return (
    <motion.div 
      className={`${sizeClasses[size]} w-full ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      whileHover={{ y: -5, transition: { duration: 0.3 } }}
    >
      <div className="aspect-[1.6/1] relative">
        {/* Main Card */}
        <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-[0_25px_80px_rgba(0,0,0,0.5)]">
          
          {/* Base gradient - darker, more luxurious */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1F15] via-[#122B1E] to-[#0D2318]" />
          
          {/* Holographic shine effect */}
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              background: 'linear-gradient(135deg, transparent 0%, rgba(195,163,101,0.1) 25%, transparent 50%, rgba(195,163,101,0.15) 75%, transparent 100%)',
            }}
          />

          {/* Top border accent */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C3A365]/60 to-transparent" />

          {/* Card Content */}
          <div className="absolute inset-0 p-6 sm:p-8 flex flex-col justify-between">
            
            {/* Top Row */}
            <div className="flex justify-between items-start">
              {/* Logo */}
              <span 
                className="text-2xl sm:text-3xl tracking-wide"
                style={{ 
                  fontFamily: 'Cormorant Garamond, serif',
                  fontWeight: 300,
                  color: '#C3A365',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}
              >
                habitué
              </span>
              
              {/* Chip */}
              <div className="w-10 h-8 sm:w-12 sm:h-10 rounded-md bg-gradient-to-br from-[#C3A365] via-[#D4B576] to-[#A8884D] opacity-80 shadow-inner" />
            </div>

            {/* Middle - Card Number */}
            <div className="flex-1 flex items-center justify-center">
              <p 
                className="text-white/40 text-lg sm:text-xl tracking-[0.3em] font-light"
                style={{ fontFamily: 'monospace' }}
              >
                {cardNumber}
              </p>
            </div>

            {/* Bottom Row */}
            <div className="flex justify-between items-end">
              {/* Balance */}
              <div>
                {showBalance && balance !== null && (
                  <>
                    <p className="text-white/30 text-[10px] uppercase tracking-[0.15em] mb-1">Solde</p>
                    <p 
                      className="text-3xl sm:text-4xl font-light text-white"
                      style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    >
                      £{typeof balance === 'number' ? balance.toFixed(2) : balance}
                    </p>
                  </>
                )}
              </div>
              
              {/* Tier Badge */}
              <p 
                className="text-sm sm:text-base font-medium tracking-wide"
                style={{ color: tierColors[tier] || tierColors['Member'] }}
              >
                {tier}
              </p>
            </div>
          </div>

          {/* Bottom border accent */}
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C3A365]/40 to-transparent" />
          
          {/* Corner accents */}
          <div className="absolute top-4 left-4 w-8 h-8 border-l border-t border-[#C3A365]/20 rounded-tl-lg" />
          <div className="absolute top-4 right-4 w-8 h-8 border-r border-t border-[#C3A365]/20 rounded-tr-lg" />
          <div className="absolute bottom-4 left-4 w-8 h-8 border-l border-b border-[#C3A365]/20 rounded-bl-lg" />
          <div className="absolute bottom-4 right-4 w-8 h-8 border-r border-b border-[#C3A365]/20 rounded-br-lg" />
        </div>

        {/* Ambient glow */}
        <div className="absolute -inset-12 bg-[#C3A365]/5 blur-3xl rounded-full -z-10" />
      </div>
    </motion.div>
  );
};

/**
 * Gift Card Variant - Shows value instead of balance
 */
export const GiftCard = ({ 
  value = 50,
  size = 'md',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'max-w-xs',
    md: 'max-w-md',
    lg: 'max-w-lg'
  };

  return (
    <motion.div 
      className={`${sizeClasses[size]} w-full ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      whileHover={{ y: -5, transition: { duration: 0.3 } }}
    >
      <div className="aspect-[1.6/1] relative">
        <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-[0_25px_80px_rgba(0,0,0,0.5)]">
          
          {/* Base gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1F15] via-[#122B1E] to-[#0D2318]" />
          
          {/* Holographic */}
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              background: 'linear-gradient(135deg, transparent 0%, rgba(195,163,101,0.1) 25%, transparent 50%, rgba(195,163,101,0.15) 75%, transparent 100%)',
            }}
          />

          {/* Top accent */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C3A365]/60 to-transparent" />

          {/* Content */}
          <div className="absolute inset-0 p-6 sm:p-8 flex flex-col justify-between">
            <div className="flex justify-between items-start">
              <span 
                className="text-2xl sm:text-3xl tracking-wide"
                style={{ 
                  fontFamily: 'Cormorant Garamond, serif',
                  fontWeight: 300,
                  color: '#C3A365',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}
              >
                habitué
              </span>
              
              {/* Gift icon */}
              <div className="w-10 h-10 rounded-full bg-[#C3A365]/10 border border-[#C3A365]/30 flex items-center justify-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C3A365" strokeWidth="1.5">
                  <rect x="3" y="8" width="18" height="13" rx="2" />
                  <path d="M12 8v13" />
                  <path d="M3 12h18" />
                  <path d="M12 8c-2-3-6-3-6 0s4 3 6 0" />
                  <path d="M12 8c2-3 6-3 6 0s-4 3-6 0" />
                </svg>
              </div>
            </div>

            <div className="text-center">
              <p 
                className="text-5xl sm:text-6xl font-light text-white"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                £{value}
              </p>
            </div>

            <div className="flex justify-between items-end">
              <p className="text-white/30 text-xs tracking-[0.15em] uppercase">
                Réseau mondial
              </p>
              <p className="text-[#C3A365]/60 text-xs tracking-wide">
                2000+ boutiques
              </p>
            </div>
          </div>

          {/* Bottom accent */}
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C3A365]/40 to-transparent" />
          
          {/* Corners */}
          <div className="absolute top-4 left-4 w-6 h-6 border-l border-t border-[#C3A365]/20 rounded-tl-lg" />
          <div className="absolute top-4 right-4 w-6 h-6 border-r border-t border-[#C3A365]/20 rounded-tr-lg" />
          <div className="absolute bottom-4 left-4 w-6 h-6 border-l border-b border-[#C3A365]/20 rounded-bl-lg" />
          <div className="absolute bottom-4 right-4 w-6 h-6 border-r border-b border-[#C3A365]/20 rounded-br-lg" />
        </div>

        <div className="absolute -inset-12 bg-[#C3A365]/5 blur-3xl rounded-full -z-10" />
      </div>
    </motion.div>
  );
};

export default PremiumCard;
